// Brand dropdown with inline creation, shared by every create-variant flow
// (variants grid, variant form modal, inline variant form). A pinned
// "+ Create" option survives react-select's filtering, so it's still there
// when the typed brand has no match — choosing it opens a small confirm
// modal (prefilled with the typed text) instead of creating on one click,
// since brands are a managed dictionary and typos shouldn't silently join it.
// The create hook invalidates the brand-options query, so the caller's
// dictionary refreshes on its own; until it lands, the new name surfaces via
// buildCanonicalOptions' "(legacy)" fallback and then turns canonical.

import { useCreateProductBrandApi } from 'apis/product-brands';
import {
  Button,
  ChakraSelect,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useToast,
} from 'design-system';
import { useState } from 'react';

const CREATE_BRAND_VALUE = '__create-brand__';

interface BrandOption {
  label: string;
  value: string;
}

interface BrandSelectProps {
  options: BrandOption[];
  value: BrandOption | null;
  onBrandChange: (brand: string) => void; // '' on clear
  placeholder?: string;
  size?: 'sm' | 'md' | 'lg';
  isInvalid?: boolean;
  isClearable?: boolean;
  menuPortalZIndex?: number;
}

export const BrandSelect = ({
  options,
  value,
  onBrandChange,
  placeholder = 'Select brand',
  size,
  isInvalid,
  isClearable,
  menuPortalZIndex = 9999,
}: BrandSelectProps) => {
  const toast = useToast();
  // Mirror of react-select's search text. Only 'input-change' updates it:
  // react-select clears its input (set-value / menu-close actions) BEFORE
  // firing onChange, so honoring those clears here would wipe the text we
  // want to prefill the modal with. Reset happens on menu open instead,
  // when the real input is empty again.
  const [inputValue, setInputValue] = useState('');
  const [draftName, setDraftName] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);

  const createApi = useCreateProductBrandApi({
    onSuccess: (brand) => {
      toast({ title: 'Brand created', status: 'success' });
      onBrandChange(brand.name);
      setIsModalOpen(false);
    },
    onError: (error: any) => {
      // the hook toasts server messages; this is the network-error fallback
      if (!error?.response?.data?.message) {
        toast({ title: 'Failed to create brand', status: 'error' });
      }
    },
  });

  const typed = inputValue.trim();
  const createOption: BrandOption = {
    value: CREATE_BRAND_VALUE,
    label: typed ? `+ Create '${typed}'` : '+ Create new brand…',
  };

  const submit = () => {
    const name = draftName.trim();
    if (!name) {
      toast({ title: 'Name is required', status: 'warning' });
      return;
    }
    createApi.mutate({ name, isActive: true });
  };

  return (
    <>
      <ChakraSelect
        size={size}
        options={[...options, createOption]}
        value={value}
        isInvalid={isInvalid}
        isClearable={isClearable}
        placeholder={placeholder}
        menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
        menuPosition='fixed'
        styles={{
          menuPortal: (base: any) => ({ ...base, zIndex: menuPortalZIndex }),
          option: (base: any, state: any) =>
            state.data?.value === CREATE_BRAND_VALUE
              ? { ...base, color: '#E53E3E', fontWeight: 600, borderTop: '1px solid #E2E8F0' }
              : base,
        }}
        filterOption={(candidate: any, input: string) =>
          candidate.value === CREATE_BRAND_VALUE || candidate.label.toLowerCase().includes(input.toLowerCase())
        }
        onMenuOpen={() => setInputValue('')}
        onInputChange={(text: string, meta: any) => {
          if (meta?.action === 'input-change') setInputValue(text);
        }}
        onChange={(opt: any) => {
          if (opt?.value === CREATE_BRAND_VALUE) {
            setDraftName(typed);
            setIsModalOpen(true);
            return;
          }
          onBrandChange(opt?.value ?? '');
        }}
      />

      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} size='md'>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>New Brand</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl isRequired>
              <FormLabel fontSize='sm'>Name</FormLabel>
              <Input
                value={draftName}
                onChange={(e) => setDraftName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') submit();
                }}
                placeholder='e.g. Almarai'
                autoFocus
              />
            </FormControl>
          </ModalBody>
          <ModalFooter gap='2'>
            <Button variant='outline' onClick={() => setIsModalOpen(false)}>
              Cancel
            </Button>
            <Button colorScheme='red' onClick={submit} isLoading={createApi.isLoading}>
              Create Brand
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};
